import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ErrorNote, Field, FormActions, FormGroup, FormPanel, TextArea, errorMessage, money, todayIso } from '@/components/ui';
import { useHrLookups, type EmployeeDetail, type PayComponentOption } from '@/hooks/hr';
import { isMoney, payInput, payProblem, payTotal, structureComponents, type PayAmounts } from './employeeRules';

/**
 * Pay for one person: the salary structure editor shared by the add form and
 * the Change pay form, and the Change pay form itself.
 */

/**
 * The salary structure: Basic first, then each allowance. An allowance set
 * as a % of basic fills itself in when basic changes, until someone types
 * their own figure into it.
 */
export function PayLinesEditor({
  components,
  amounts,
  onChange,
}: {
  components: PayComponentOption[];
  amounts: PayAmounts;
  onChange: (next: PayAmounts) => void;
}) {
  const [typed, setTyped] = useState<Set<string>>(() => new Set(Object.keys(amounts)));

  function set(c: PayComponentOption, value: string) {
    const next = { ...amounts, [c.id]: value };
    if (c.systemCode === 'BASIC') {
      const basic = isMoney(value) ? Number(value) : 0;
      for (const other of components) {
        if (other.percentOfBasic && !typed.has(other.id)) {
          next[other.id] = basic > 0 ? String(Math.round(basic * Number(other.percentOfBasic)) / 100) : '';
        }
      }
    } else {
      setTyped((s) => new Set(s).add(c.id));
    }
    onChange(next);
  }

  if (components.length === 0) return <p className="muted">Loading pay items…</p>;

  return (
    <>
      {components.map((c) => (
        <Field
          key={c.id}
          label={c.systemCode === 'BASIC' ? 'Basic pay' : c.nameEn}
          type="number"
          min={0}
          required={c.systemCode === 'BASIC'}
          value={amounts[c.id] ?? ''}
          onChange={(v) => set(c, v)}
          hint={c.percentOfBasic ? `Usually ${Number(c.percentOfBasic)}% of basic` : undefined}
        />
      ))}
      <p className="formgrid__full employees__total">
        Monthly pay in all: <strong>{money(String(payTotal(amounts)))}</strong>
      </p>
    </>
  );
}

/**
 * Change pay: raises a pay change from a date. Someone else approves it.
 * Starts from the person's current pay, where the caller can see it.
 */
export function ChangePayForm({
  employee,
  onCancel,
  onSaved,
}: {
  employee: EmployeeDetail;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const lookups = useHrLookups();
  const components = structureComponents(lookups.data?.payComponents);
  const [amounts, setAmounts] = useState<PayAmounts>(() => {
    const out: PayAmounts = {};
    for (const l of employee.pay?.lines ?? []) if (l.payAmount) out[l.componentId] = String(Number(l.payAmount));
    return out;
  });
  const [dailyRate, setDailyRate] = useState(
    employee.pay?.dailyRate ? String(Number(employee.pay.dailyRate)) : employee.dailyRate ? String(Number(employee.dailyRate)) : '',
  );
  const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.post<{ id: string; documentNo: string }>(`/api/v1/hr/employees/${employee.id}/pay-changes`, {
        effectiveFrom,
        pay: payInput(employee.workerType, amounts, dailyRate),
        reason: reason.trim(),
      }),
    onSuccess: (r) => onSaved(`${r.documentNo} raised for ${employee.nameEn}. Someone else must approve it.`),
    onError: (e) => setProblem(errorMessage(e, 'Could not save the pay change')),
  });

  function submit() {
    if (!effectiveFrom) return setProblem('Choose the date the new pay starts from.');
    if (effectiveFrom < employee.joinedOn) return setProblem('The new pay cannot start before they joined.');
    const p = payProblem(employee.workerType, components, amounts, dailyRate);
    if (p) return setProblem(p);
    if (!reason.trim()) return setProblem('Say why pay is changing.');
    setProblem(null);
    save.mutate();
  }

  const current =
    employee.workerType === 'DAILY'
      ? employee.dailyRate
        ? `${money(employee.dailyRate)} a day`
        : null
      : employee.salary
        ? `${money(employee.salary)} a month`
        : null;

  return (
    <FormPanel
      title={`Change pay — ${employee.code} ${employee.nameEn}`}
      icon={<AppIcon kind="payroll" />}
      intro="Someone other than you approves it. A date in an already-paid month adds back pay (arrears) to the next pay run."
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <FormGroup title="When and why" columns={2} hint={current ? `Paid now: ${current}.` : undefined}>
          <Field label="New pay starts from" type="date" required value={effectiveFrom} onChange={setEffectiveFrom} />
          <TextArea label="Why" required value={reason} onChange={setReason} rows={2} placeholder="e.g. Yearly increase" />
        </FormGroup>
        {employee.workerType === 'DAILY' ? (
          <FormGroup title="New daily rate" columns={3}>
            <Field label="Daily rate" type="number" min={0} required value={dailyRate} onChange={setDailyRate} />
          </FormGroup>
        ) : (
          <FormGroup title="New monthly pay" columns={3} hint="Leave an item empty if it is not paid.">
            <PayLinesEditor components={components} amounts={amounts} onChange={setAmounts} />
          </FormGroup>
        )}
        {lookups.error && <ErrorNote message={lookups.error.message} />}
        {problem && <ErrorNote message={problem} />}
        <FormActions onCancel={onCancel}>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Send for approval'}
          </button>
        </FormActions>
      </form>
    </FormPanel>
  );
}
